import Pjax from 'pjax';
import { querySelectArray } from './utils';

const DEFAULT_TRANSITION = 'all 0s ease 0s';
const MAX_PJAX_WAIT_TIME_MS = 15000;
const START_TRANSITION_DURATION_MS = 1000;
const END_TRANSITION_DURATION_MS = 500;
const START_TRANSITION = `transform ${START_TRANSITION_DURATION_MS}ms`;
const END_TRANSITION = `opacity ${END_TRANSITION_DURATION_MS}ms`;
const MIN_PJAX_WAIT_TIME_MS =
    START_TRANSITION_DURATION_MS + END_TRANSITION_DURATION_MS;
const SCALE_BUFFER = 2; // multiply scaling math by this to add a buffer for scaling so that the corners of the circle aren't visible
const OPTIMISTIC_LANG_TOGGLE = 'data-lang-toggle-on';

const THEMES = {
    _DEFAULT: '#006699',
    TopicGovernmentPage: '#fa9027',
    TopicEducationPage: '#3966a2',
    TopicJobsPage: '#176f7a',
    TopicHousingPage: '#fab116',
    TopicSelfCarePage: '#d93745',
    TopicMoneyPage: '#4ea540',
    TopicDriverLicencePage: '#bb2ebd'
};

const InitPjax = () => {
    function initSearchBox() {
        const searchForms = document.querySelectorAll('form[role="search"]');
        searchForms.forEach(searchForm => {
            if (searchForm.hasCustomTranslatedError) {
                console.info(
                    'Search form already has translated error. Bailing...',
                );
                return;
            }
            searchForm.hasCustomTranslatedError = true;
            const searchBox = searchForm.querySelector('[data-searchbox-ref]');
            let translations = {
                searchBoxRequired: 'Please fill in the search field',
            };
            const translationsEl = searchForm.querySelector(
                '[data-translations]',
            );
            if (translationsEl) {
                translations = {
                    ...translations,
                    ...JSON.parse(
                        translationsEl.getAttribute('data-translations'),
                    ),
                };
            }

            function updateSearchError() {
                const value = searchBox.value;
                searchBox.setCustomValidity(
                    value.length > 0 ? '' : translations.searchBoxRequired,
                );
            }

            searchBox.addEventListener('keyup', updateSearchError);

            updateSearchError();
        });
    }
    initSearchBox();

    if (
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
        console.info(
            'Browser "prefers-reduced-motion: reduce" which we will obey',
        );
        // DEV NOTE: We'll also disable Pjax in this scenario
        // because maintaining a low-animation variation of
        // Pjax transitions would be difficult, and this way
        // they get a normal page load which isn't too bad anyway.
        return;
    }

    addOptimisticToggleHandlers();
    setToggleTitle(
        document.documentElement.getAttribute('lang') === 'en-NZ' ? 'en' : 'mi',
    );

    let navigatedByLangToggle = false;

    document.addEventListener('pjax:send', pjaxSend);
    document.addEventListener('pjax:complete', pjaxComplete);
    document.addEventListener('pjax:error', pjaxError);
    const div = document.createElement('div');
    document.body.appendChild(div); // temporarily attach to the bottom of the page
    div.className = 'page-swipe';
    div.style.display = 'block';
    const DIV_SIZE = Math.max(div.offsetWidth, div.offsetHeight); // calculate size for scaling math later on
    document.body.insertBefore(div, document.body.firstChild); // but then move it to the top of the page
    div.addEventListener('transitionend', divTransitionEnd);

    document.addEventListener('click', onClick, false);

    let scaleTo = 'scale(30)'; // as a default
    let animationState = false; // false | "start" | "end"
    let timer;
    let hasPageLoaded;
    let animationStartedAt;
    const scrollPositions = {};

    const pjax = new Pjax({
        elements: 'a[href]:not([href*="feedback"]):not([href*="urupare"])',
        selectors: [
            'title',
            'meta[name=description]',
            'meta[name=keywords]',
            '.skip-link',
            '.menubutton-content li',
            '[data-lang-toggle-link]',
            'form[role="search"]',
            '.main',
            '[data-reload-link]',
        ],
        scrollRestoration: false, // enabling this breaks fragment links to parts of the page (from new windows)
        switches: {
            '.main'(oldEl, newEl, options) {
                let waitForMs = 0;
                if (animationState !== false && animationStartedAt) {
                    const sinceClickMs = Date.now() - animationStartedAt;
                    // Ensure that the animation has time to complete if the
                    // network was fast
                    waitForMs = Math.max(
                        START_TRANSITION_DURATION_MS - sinceClickMs,
                        0,
                    );
                }
                setTimeout(() => {
                    oldEl.outerHTML = newEl.outerHTML;
                    this.onSwitch();
                }, waitForMs);
            },
            '[data-lang-toggle-link]'(oldEl, newEl, options) {
                const newHref = newEl.getAttribute('href');
                oldEl.setAttribute('href', newHref);
                const isLangToggleMaori =
                    newHref.indexOf('/mi/') !== -1 ||
                    newHref.indexOf('/mi_NZ/') !== -1;
                if (isLangToggleMaori) {
                    // if linking to Maori that means that the new page should be in English
                    document.documentElement.setAttribute('lang', 'en-NZ');
                    setToggleTitle('en');
                } else {
                    document.documentElement.setAttribute('lang', 'mi');
                    setToggleTitle('mi');
                }
                removeOptimisticToggles();
                if (navigatedByLangToggle) {
                    // maintain focus on lang toggle
                    setTimeout(() => {
                        addOptimisticToggleHandlers();
                        oldEl.focus();
                    }, 30);
                }
                this.onSwitch();
            },
            'form[role="search"]'(oldEl, newEl, options) {
                const searchBox = oldEl.querySelector('[data-searchbox-ref]');
                let searchTerm = '';
                if (searchBox) {
                    searchTerm = searchBox.value;
                }
                oldEl.outerHTML = newEl.outerHTML;
                if (searchTerm) {
                    const searchBoxes = document.querySelectorAll(
                        '[data-searchbox-ref]',
                    );
                    searchBoxes.forEach(searchBox => {
                        searchBox.value = searchTerm;
                    });
                }
                initSearchBox();
                this.onSwitch();
            },
        },
        cacheBust: false,
        analytics: () => {
            // _gaq may not be loaded due to environment or adblocker
            if (window._gaq) {
                // fire global google analytics function initialised on first pageload
                window._gaq.push(['_trackPageview']);
            }
        },
    });

    function setToggleTitle(lang) {
        const langToggleLink = document.querySelector(
            '[data-lang-toggle-link]',
        );
        const str = langToggleLink.getAttribute(`data-title-${lang}`);
        langToggleLink.setAttribute('title', str);
        langToggleLink.setAttribute('aria-label', str);
    }

    function onClick(e) {
        animationState = false;
        const isInHyperlink = !!isInsideElement(['a'], e.target);
        const card = isInsideClass(
            ['block__link', 'menubutton-content__link--in-mobilemenu', 'card'],
            e.target,
        );
        if (isInHyperlink && card) {
            // Don't animate if user does contro0l/command+click to open link in a new tab
            if (e.metaKey || e.ctrlKey) return;

            if (!div.parentNode) {
                // then it's been removed from the DOM in the ESC handler
                // so don't both with the animation
                console.info(
                    '.page-swipe not attached to the page. Exiting early',
                );
                return;
            }
            const TRANSITION_SIZE =
                Math.max(window.innerWidth, window.innerHeight) * 2; // largest dimension * 2 because the circle's center is based on click position which might start from the bottom-right of the screen and so we need at least double the screen size to cover it
            scaleTo = `scale(${(TRANSITION_SIZE / DIV_SIZE) * SCALE_BUFFER})`; // 300px scaled to 15 would be 3900
            hasPageLoaded = Date.now();
            div.style.display = 'block';
            div.style.opacity = 1;

            const transitionColorRef = card.getAttribute(
                'data-transition-color',
            );

            let transitionColor = '#f5f5f5';
            if (transitionColorRef) {
                transitionColor = THEMES[transitionColorRef];
            }

            div.style.backgroundColor = transitionColor;
            div.style.transition = DEFAULT_TRANSITION;
            div.style.transform = `scale(0.01)`;
            div.style.left = `${e.clientX}px`;
            div.style.top = `${e.clientY}px`;
            if (timer) {
                clearTimeout(timer);
            }

            timer = setTimeout(() => {
                animationState = 'start';
                animationStartedAt = Date.now();
                document.body.style.overflow = 'hidden';
                div.style.transition = START_TRANSITION;
                div.style.transform = scaleTo;
            }, 60);
        }
    }

    function pjaxComplete(e) {
        hasPageLoaded = true;
        if (
            navigatedByLangToggle &&
            scrollPositions[window.location.toString()]
        ) {
            setTimeout(() => {
                window.scrollTo(0, scrollPositions[window.location.toString()]);
                if (window.grecaptcha) {
                    grecaptcha.reset();
                }
            }, 100);
        }
    }

    // TODO - this isn't currently active as arg is 'e' and function uses 'event'
    function pjaxSend(e) {
        if (!event.triggerElement) {
            // when navigating by browser forward/back
            return;
        }
        navigatedByLangToggle = event.triggerElement.hasAttribute(
            'data-lang-toggle-link',
        );
    }

    function pjaxError(e, e2) {
        // Fired when pjax request fails...typically due to a network error,
        // server error, etc.
        //
        // Because this function is called it's a JS error displayed in the
        // console and so regular users won't see it, and there's a question
        // about how we surface these errors to regular users.
        //
        // Preferably we'd display the browser's conventional network error
        // page to help people diagnose their problem so we'll reload the
        // current page and hopefully trigger the same error to display that.
        window.location.reload(true);
    }

    function divTransitionEnd() {
        if (timer) {
            clearTimeout(timer);
        }
        // eslint-disable-next-line default-case
        switch (animationState) {
            case 'start': {
                if (animationStartedAt + MIN_PJAX_WAIT_TIME_MS > Date.now()) {
                    // Page has loaded too quickly, so to avoid making sudden
                    // page transition effects (which would appear flashy)
                    // we'll have a minimum time for animations
                    timer = setTimeout(divTransitionEnd, 30); // 30ms approx 2 frames at 60fps
                    return;
                }
                animationState = 'end';
                div.style.transition = END_TRANSITION;
                document.body.style.overflow = 'auto'; // DEV NOTE: restoring scrollbars here so that the page itself doesn't jutter around when scrollbars are added to the width again
                div.style.display = 'block';
                div.style.opacity = 0;
                timer = setTimeout(divTransitionEnd, 2500); // Safety function... expect animation to finish within this time
                break;
            }
            case 'end': {
                if (
                    hasPageLoaded !== true &&
                    hasPageLoaded + MAX_PJAX_WAIT_TIME_MS < Date.now()
                ) {
                    // Still waiting for page to load so wait until Pjax is ready...
                    timer = setTimeout(divTransitionEnd, 30);
                    return;
                }

                animationState = false;
                hasPageLoaded = false;
                div.style.transition = DEFAULT_TRANSITION;
                div.style.display = 'none';
                div.style.opacity = 0;
                break;
            }
        }
    }

    function removeOptimisticToggles() {
        const langToggleLinks = querySelectArray(
            `[${OPTIMISTIC_LANG_TOGGLE}]`,
            document,
        );
        langToggleLinks.forEach(link => {
            link.removeAttribute(OPTIMISTIC_LANG_TOGGLE);
        });
    }

    function addOptimisticToggleHandlers() {
        const langToggleLinks = querySelectArray(
            '[data-lang-toggle-link]',
            document,
        );
        langToggleLinks.forEach(link => {
            const alreadyAttached = link.getAttribute('data-optimistic-load');
            if (!alreadyAttached) {
                link.addEventListener('click', onLangToggle, false);
                link.setAttribute('data-optimistic-load', 'true');
            }
        });
        function onLangToggle(e) {
            scrollPositions[window.location.toString()] = window.scrollY;
            e.target.parentNode.toggleAttribute(OPTIMISTIC_LANG_TOGGLE);
            return false;
        }
    }
};

function isInsideClass(classNames, target) {
    let currentTarget = target;
    while (currentTarget.parentNode) {
        if (
            classNames.some(className =>
                currentTarget.classList.contains(className),
            )
        ) {
            return currentTarget;
        }
        currentTarget = currentTarget.parentNode;
    }
    return false;
}

function isInsideElement(elementNames, target) {
    let currentTarget = target;
    while (currentTarget.parentNode) {
        if (
            elementNames.some(
                elementName =>
                    currentTarget.nodeName.toLowerCase() === elementName,
            )
        ) {
            return currentTarget;
        }
        currentTarget = currentTarget.parentNode;
    }
    return false;
}

export default InitPjax;
